import { z } from 'zod';
import 'date-fns-tz';
import { DEMO_SESSION_DURATION_VALUES } from '@lgc/database/types/domains/club';
import { AUDIENCE_CLASS_VALUES } from '@lgc/database/types/domains/bay';

// src/pricing/tax/calculateTax.ts
var RATE_UNITS_PER_PERCENT = 1e6;
function toCents(dollars) {
  const cents = dollars * 100;
  return cents >= 0 ? Math.floor(cents + 0.5) : -Math.floor(-cents + 0.5);
}
function calculateTaxCents(baseCents, taxRatePercent) {
  if (!Number.isInteger(baseCents)) {
    throw new RangeError(`calculateTaxCents: base must be integer cents, got ${baseCents}`);
  }
  if (!Number.isFinite(taxRatePercent)) {
    throw new RangeError(`calculateTaxCents: tax rate must be a finite number, got ${taxRatePercent}`);
  }
  const rateUnits = Math.round(taxRatePercent * RATE_UNITS_PER_PERCENT);
  const sign = Math.sign(baseCents) * Math.sign(rateUnits);
  if (sign === 0) return 0;
  const numerator = BigInt(Math.abs(baseCents)) * BigInt(Math.abs(rateUnits));
  const denominator = BigInt(100 * RATE_UNITS_PER_PERCENT);
  const quotient = numerator / denominator;
  const roundsUp = numerator % denominator * BigInt(2) >= denominator;
  return sign * Number(roundsUp ? quotient + BigInt(1) : quotient);
}
function calculateTax(baseAmount, taxRatePercent) {
  const baseCents = toCents(baseAmount);
  const taxCents = calculateTaxCents(baseCents, taxRatePercent);
  return {
    taxRatePercent,
    taxAmount: taxCents / 100,
    totalWithTax: (baseCents + taxCents) / 100
  };
}

// src/pricing/fees/calculateProcessingFee.ts
var DEFAULT_PROCESSING_FEE_RATE = {
  ratePercent: 2.9,
  fixed: 0.3
};
var DEFAULT_PROCESSING_FEE_CONFIG = {
  card: DEFAULT_PROCESSING_FEE_RATE,
  bank: { ratePercent: 0, fixed: 0 }
};
function getProcessingFeeConfig(_club) {
  return DEFAULT_PROCESSING_FEE_CONFIG;
}
var PROCESSING_FEE_TAXABLE = false;
function isProcessingFeeTaxable(_club) {
  return PROCESSING_FEE_TAXABLE;
}
function calculateProcessingFee(baseAmount, rate) {
  if (!(baseAmount > 0)) {
    return {
      feeRatePercent: rate.ratePercent,
      feeFixed: rate.fixed,
      feeAmount: 0,
      totalWithFee: round2(Math.max(0, baseAmount))
    };
  }
  const rateFraction = rate.ratePercent / 100;
  const charged = rateFraction < 1 ? (baseAmount + rate.fixed) / (1 - rateFraction) : baseAmount;
  const feeAmount = charged - baseAmount;
  return {
    feeRatePercent: rate.ratePercent,
    feeFixed: rate.fixed,
    feeAmount: round2(feeAmount),
    totalWithFee: round2(charged)
  };
}
function round2(n) {
  return Math.round(n * 100) / 100;
}
function resolveQuoteProcessingFee(args) {
  const { passProcessingFees, provider, taxableBase, methodType = "card" } = args;
  if (!passProcessingFees || provider !== "stripe") {
    return { feeAmount: 0, feeRatePercent: 0 };
  }
  const config = getProcessingFeeConfig();
  const rate = methodType === "bank" ? config.bank : config.card;
  return {
    feeAmount: calculateProcessingFee(taxableBase, rate).feeAmount,
    feeRatePercent: rate.ratePercent
  };
}

// src/pricing/fees/sizeCardCharge.ts
function sizeCardCharge(input) {
  const baseCents = Math.max(0, Math.round(input.baseCents));
  const preFeeCents = Math.max(0, Math.round(input.preFeeCents));
  const storedFeeCents = Math.max(0, Math.round(input.storedFeeCents));
  const balanceCents = Math.max(0, Math.round(input.balanceCents));
  if (preFeeCents === 0) {
    return { creditCents: 0, feeCents: 0, adjustmentCents: 0 - storedFeeCents, cardCents: 0, minimumAdjusted: false };
  }
  const feeFor = (creditCents2) => {
    if (creditCents2 === 0 && input.pricedForSettlingMethod) return storedFeeCents;
    if (!input.rate) return 0;
    const cardShareCents = preFeeCents - creditCents2;
    const feeBaseDollars = baseCents * cardShareCents / preFeeCents / 100;
    const feeCents2 = Math.round(calculateProcessingFee(feeBaseDollars, input.rate).feeAmount * 100);
    return Math.min(feeCents2, storedFeeCents);
  };
  let creditCents = Math.min(balanceCents, preFeeCents);
  let feeCents = feeFor(creditCents);
  let cardCents = preFeeCents - creditCents + feeCents;
  let minimumAdjusted = false;
  if (cardCents > 0 && cardCents < input.minChargeCents) {
    minimumAdjusted = true;
    if (preFeeCents >= input.minChargeCents) {
      creditCents = preFeeCents - input.minChargeCents;
      feeCents = feeFor(creditCents);
      cardCents = input.minChargeCents + feeCents;
    } else {
      creditCents = 0;
      feeCents = feeFor(0);
      cardCents = preFeeCents + feeCents;
      if (cardCents > 0 && cardCents < input.minChargeCents) {
        feeCents = storedFeeCents;
        cardCents = preFeeCents + feeCents;
      }
    }
  }
  return {
    creditCents,
    feeCents,
    adjustmentCents: feeCents - storedFeeCents,
    cardCents,
    minimumAdjusted
  };
}
var RatesByDurationSchema = z.object({
  "30": z.number().int().positive("Price must be positive").nullable(),
  "60": z.number().int().positive("Price must be positive").nullable(),
  "90": z.number().int().positive("Price must be positive").nullable(),
  "120": z.number().int().positive("Price must be positive").nullable(),
  "150": z.number().int().positive("Price must be positive").nullable(),
  "180": z.number().int().positive("Price must be positive").nullable()
}).refine(
  (data) => Object.values(data).some((price) => price !== null),
  { message: "At least one duration must have a non-null price" }
);
function validateRates(rates) {
  const result = RatesByDurationSchema.safeParse(rates);
  if (result.success) {
    return { success: true, data: result.data };
  }
  const errors = result.error.errors.map((err) => {
    const path = err.path.join(".");
    return `${path}: ${err.message}`;
  });
  return { success: false, errors };
}

// src/pricing/shared/currency.ts
function formatPriceFromCents(cents, wholeNumber) {
  if (cents === null) return "N/A";
  const dollars = cents / 100;
  if (wholeNumber && Number.isInteger(dollars)) {
    return `$${dollars}`;
  }
  return `$${dollars.toFixed(2)}`;
}
function dollarsToCents(dollars) {
  return Math.round(dollars * 100);
}
function centsToDollars(cents) {
  return cents / 100;
}
var DEFAULT_CURRENCY = "USD";

// src/pricing/shared/providerMinimum.ts
var PROVIDER_MIN_CHARGE_AMOUNT_CENTS = 100;
var PROVIDER_MIN_CHARGE_AMOUNT_DOLLARS = PROVIDER_MIN_CHARGE_AMOUNT_CENTS / 100;
var PROVIDER_MIN_CHARGE_MESSAGE = `Price must be $0.00 or at least $${PROVIDER_MIN_CHARGE_AMOUNT_DOLLARS.toFixed(2)}.`;
function clampChargeableCents(cents) {
  if (cents <= 0) return { cents: 0, raised: false, floored: cents < 0 };
  if (cents < PROVIDER_MIN_CHARGE_AMOUNT_CENTS) {
    return { cents: PROVIDER_MIN_CHARGE_AMOUNT_CENTS, raised: true, floored: false };
  }
  return { cents, raised: false, floored: false };
}

// src/pricing/shared/representativeHourlyRate.ts
function representativeHourlyRateCents(club) {
  if (!club) return null;
  switch (club.pricingMode) {
    case "scalingPrice":
      return club.hourlyRateCents ?? null;
    // The engine resolves a *booking's* rate from the layer stack; there is no
    // single rate for a club. `hourlyRateCents` is kept populated under this
    // mode as the seed and restore value, which makes it the right stand-in.
    case "dynamicPricing":
      return club.hourlyRateCents ?? null;
    case "setPrice": {
      const rateDollars = club.defaultPricing?.["60"] ?? null;
      return rateDollars !== null ? dollarsToCents(rateDollars) : null;
    }
    // Undefined or unrecognised. Prefer the hourly rate if the projection
    // carried one — it is right for both live hourly modes — and otherwise
    // decline to answer rather than guessing an arm. The old code fell into
    // `setPrice` here and read a null table, which is what refused the saves.
    default:
      return club.hourlyRateCents ?? null;
  }
}

// src/pricing/shared/subscriptionPriceBounds.ts
var PRICE_MAX_DOLLARS = 1e5;
var PRICE_MAX_MESSAGE = `Price must be $${PRICE_MAX_DOLLARS.toLocaleString("en-US", {
  minimumFractionDigits: 2
})} or less.`;

// src/datetime/errors.ts
var DateTimeLibraryError = class extends Error {
  constructor(type, message, details) {
    super(message);
    this.name = "DateTimeLibraryError";
    this.type = type;
    this.details = details;
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
};
var InvalidDateError = class extends DateTimeLibraryError {
  constructor(message) {
    super("INVALID_DATE", message);
    this.name = "InvalidDateError";
  }
};

// src/datetime/timeString.ts
var END_OF_DAY = "24:00";
function isEndOfDaySentinel(timeString) {
  return timeString === END_OF_DAY || timeString === "24:00:00";
}
var MAX_OVERFLOW_HOUR = 47;
var TIME_PATTERN = /^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/;
function parseTimeOfDay(timeString, options = {}) {
  if (!timeString || typeof timeString !== "string") {
    throw new InvalidDateError("Time string must be a non-empty string");
  }
  const match = TIME_PATTERN.exec(timeString);
  if (!match) {
    throw new InvalidDateError(
      `Invalid time format: ${timeString}. Expected HH:mm or HH:mm:ss`
    );
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = match[3] === void 0 ? 0 : Number(match[3]);
  if (minutes > 59) {
    throw new InvalidDateError(`Invalid minute: ${minutes}. Expected 0-59`);
  }
  if (seconds > 59) {
    throw new InvalidDateError(`Invalid second: ${seconds}. Expected 0-59`);
  }
  if (hours > 23) {
    if (options.allowDayOverflow) {
      if (hours > MAX_OVERFLOW_HOUR) {
        throw new InvalidDateError(
          `Invalid hour: ${hours}. Expected 0-${MAX_OVERFLOW_HOUR} when day overflow is allowed`
        );
      }
      return { hours, minutes, seconds };
    }
    if (options.allowEndOfDay && hours === 24 && minutes === 0 && seconds === 0) {
      return { hours, minutes, seconds };
    }
    if (hours === 24 && minutes === 0 && seconds === 0) {
      throw new InvalidDateError(
        `Hour 24 is the end-of-day sentinel, not a wall-clock time. Use createClubDateTimeFromSlot() for slot times that may be "${END_OF_DAY}".`
      );
    }
    throw new InvalidDateError(
      `Invalid hour: ${hours}. Expected 0-23${options.allowEndOfDay ? ` (or ${END_OF_DAY} for end-of-day)` : ""}`
    );
  }
  return { hours, minutes, seconds };
}

// src/datetime/converters.ts
function timeStringToMinutes(timeString) {
  if (isEndOfDaySentinel(timeString)) {
    return 1440;
  }
  const { hours, minutes } = parseTimeOfDay(timeString);
  return hours * 60 + minutes;
}

// src/pricing/shared/timeUtils.ts
var MINUTES_PER_DAY = 1440;
var DAY_NAMES = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday"
];
function getDayNameInTimezone(utcDate, timezone) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short"
  });
  const weekdayShort = formatter.format(utcDate);
  const mapping = {
    Sun: "sunday",
    Mon: "monday",
    Tue: "tuesday",
    Wed: "wednesday",
    Thu: "thursday",
    Fri: "friday",
    Sat: "saturday"
  };
  const day = mapping[weekdayShort];
  if (!day) {
    throw new Error(
      `Unexpected weekday string "${weekdayShort}" from Intl.DateTimeFormat for date ${utcDate.toISOString()} in timezone ${timezone}`
    );
  }
  return day;
}
function getMinutesFromMidnightInTimezone(utcDate, timezone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "numeric",
    hour12: false
  }).formatToParts(utcDate).reduce(
    (acc, p) => {
      if (p.type === "hour") acc.hour = parseInt(p.value, 10);
      if (p.type === "minute") acc.minute = parseInt(p.value, 10);
      return acc;
    },
    { hour: 0, minute: 0 }
  );
  const hour = parts.hour === 24 ? 0 : parts.hour;
  return hour * 60 + parts.minute;
}
function isTimeInSegments(timeMinutes, segments) {
  return segments.some(
    (seg) => timeMinutes >= seg.startMinutes && timeMinutes < seg.endMinutes
  );
}
function isTimeInPreviousDayOverflow(timeMinutes, previousDaySegments) {
  return previousDaySegments.some((seg) => {
    if (seg.endMinutes <= MINUTES_PER_DAY) return false;
    const overflowEndMinutes = seg.endMinutes - MINUTES_PER_DAY;
    return timeMinutes < overflowEndMinutes;
  });
}
function minutesToTime(minutes) {
  const hours = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
}
function crossesMidnight(startTime, endTime) {
  const startMinutes = timeStringToMinutes(startTime);
  const endMinutes = timeStringToMinutes(endTime);
  return endMinutes < startMinutes;
}
function calculateDurationWithMidnight(startTime, endTime, endsNextDay) {
  const startMinutes = timeStringToMinutes(startTime);
  const endMinutes = timeStringToMinutes(endTime);
  const shouldAddDay = endsNextDay ?? endMinutes < startMinutes;
  if (shouldAddDay) {
    return 1440 - startMinutes + endMinutes;
  }
  return endMinutes - startMinutes;
}

// src/pricing/primeTime/resolver.ts
function resolvePrimeTimeForBooking(bookingStartTimeUtc, clubTimezone, primeTimeConfig, primeTimeEnabled) {
  if (!primeTimeEnabled || !primeTimeConfig?.hours || !primeTimeConfig?.rates) {
    return { isPrimeTime: false, primeTimeRates: void 0 };
  }
  const rates = primeTimeConfig.rates;
  const dayName = getDayNameInTimezone(bookingStartTimeUtc, clubTimezone);
  const minutesFromMidnight = getMinutesFromMidnightInTimezone(
    bookingStartTimeUtc,
    clubTimezone
  );
  const daySegments = primeTimeConfig.hours[dayName];
  if (daySegments && daySegments.length > 0) {
    if (isTimeInSegments(minutesFromMidnight, daySegments)) {
      return { isPrimeTime: true, primeTimeRates: rates };
    }
  }
  const prevDayIndex = (DAY_NAMES.indexOf(dayName) + 6) % 7;
  const prevDaySegments = primeTimeConfig.hours[DAY_NAMES[prevDayIndex]];
  if (prevDaySegments && prevDaySegments.length > 0) {
    if (isTimeInPreviousDayOverflow(minutesFromMidnight, prevDaySegments)) {
      return { isPrimeTime: true, primeTimeRates: rates };
    }
  }
  return {
    isPrimeTime: false,
    primeTimeRates: rates
  };
}
var TimeSegmentSchema = z.object({
  startMinutes: z.number().int("Start minutes must be an integer").min(0, "Start minutes cannot be negative").max(1439, "Start minutes cannot exceed 1439 (11:59 PM)"),
  endMinutes: z.number().int("End minutes must be an integer").min(0, "End minutes cannot be negative").max(1560, "End minutes cannot exceed 1560 (2:00 AM next day)")
}).refine(
  (data) => data.startMinutes < data.endMinutes,
  { message: "Start time must be before end time" }
).refine(
  // An open window is owned by the day it opens and may spill at most into the
  // next day, so it can never be longer than 24h. endMinutes === startMinutes+1440
  // is a window that closes exactly 24h later (e.g. {0,1440} = the 24/7 day).
  (data) => data.endMinutes <= data.startMinutes + 1440,
  { message: "A window cannot be longer than 24 hours" }
);
var DayScheduleSchema = z.array(TimeSegmentSchema).refine(
  // Segments within a single day must not overlap. Sorted by start, each segment
  // must begin at or after the previous one ends (spillover endMinutes > 1440 is
  // accounted for since the comparison uses raw endMinutes).
  (segments) => {
    const sorted = [...segments].sort((a, b) => a.startMinutes - b.startMinutes);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].startMinutes < sorted[i - 1].endMinutes) return false;
    }
    return true;
  },
  { message: "Schedule segments must not overlap" }
);
var WeeklyScheduleSchema = z.object({
  monday: DayScheduleSchema,
  tuesday: DayScheduleSchema,
  wednesday: DayScheduleSchema,
  thursday: DayScheduleSchema,
  friday: DayScheduleSchema,
  saturday: DayScheduleSchema,
  sunday: DayScheduleSchema
});
var DateStringSchema = z.string().regex(
  /^\d{4}-\d{2}-\d{2}$/,
  "Date must be in YYYY-MM-DD format"
);
var ScheduleExceptionKindSchema = z.enum(["hours", "closed"]);
var ScheduleExceptionSchema = z.object({
  date: DateStringSchema,
  kind: ScheduleExceptionKindSchema,
  schedule: DayScheduleSchema,
  reason: z.string().optional(),
  isPublic: z.boolean().optional()
}).refine(
  (data) => data.schedule.length > 0,
  { message: 'An exception must declare at least one segment \u2014 closed all day is kind "closed" with 00:00\u201324:00' }
);
z.record(
  DateStringSchema,
  ScheduleExceptionSchema
);
var MAX_DEMO_ADVANCE_MINUTES = 30 * 24 * 60;
var DemoBookingConfigSchema = z.object({
  minAdvanceMinutes: z.number().int("Minimum advance notice must be a whole number of minutes").min(0, "Minimum advance notice cannot be negative").max(MAX_DEMO_ADVANCE_MINUTES, "Minimum advance notice cannot exceed 30 days"),
  // Driven by the values array, not by indexing its slots — adding a third
  // allowed duration widens this schema with the TS type and the settings
  // radio in one edit, instead of silently rejecting the new option.
  sessionDurationMinutes: z.custom(
    (n) => typeof n === "number" && DEMO_SESSION_DURATION_VALUES.includes(n),
    { message: `Session duration must be one of: ${DEMO_SESSION_DURATION_VALUES.join(", ")}` }
  ),
  hours: WeeklyScheduleSchema
});
z.object({
  enabled: z.boolean().optional(),
  config: DemoBookingConfigSchema.optional()
}).refine(
  (body) => body.enabled !== void 0 || body.config !== void 0,
  { message: "At least one field is required: enabled or config" }
);
var AudienceEntrySchema = z.union([
  z.object({ kind: z.enum(AUDIENCE_CLASS_VALUES) }).strict(),
  z.object({ kind: z.literal("plan"), planType: z.literal("membership"), id: z.number().int().positive() }).strict()
]);
var BayAccessSegmentSchema = z.object({
  startMinutes: z.number().int().min(0).max(1439),
  endMinutes: z.number().int().min(0).max(1560),
  audience: z.array(AudienceEntrySchema).min(1, "Choose who may book in this window")
}).strict().refine((s) => s.startMinutes < s.endMinutes, { message: "Start time must be before end time" }).refine((s) => s.endMinutes <= s.startMinutes + 1440, { message: "A window cannot be longer than 24 hours" }).refine(
  (s) => new Set(s.audience.map((a) => a.kind === "plan" ? `plan:${a.id}` : a.kind)).size === s.audience.length,
  { message: "An audience lists each entry once" }
);
var BayAccessDaySchema = z.array(BayAccessSegmentSchema).min(1, "A customized day needs at least one window").refine(
  (segments) => {
    const sorted = [...segments].sort((a, b) => a.startMinutes - b.startMinutes);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].startMinutes < sorted[i - 1].endMinutes) return false;
    }
    return true;
  },
  { message: "Windows on one day must not overlap" }
);
z.object({
  monday: BayAccessDaySchema.optional(),
  tuesday: BayAccessDaySchema.optional(),
  wednesday: BayAccessDaySchema.optional(),
  thursday: BayAccessDaySchema.optional(),
  friday: BayAccessDaySchema.optional(),
  saturday: BayAccessDaySchema.optional(),
  sunday: BayAccessDaySchema.optional()
}).strict();

// src/pricing/primeTime/schemas.ts
var PrimeTimeConfigSchema = z.object({
  hours: WeeklyScheduleSchema,
  rates: RatesByDurationSchema
});
function validatePrimeTimeConfig(config) {
  const result = PrimeTimeConfigSchema.safeParse(config);
  if (result.success) {
    return { success: true, data: result.data };
  }
  const errors = result.error.errors.map((err) => {
    const path = err.path.join(".");
    return `${path}: ${err.message}`;
  });
  return { success: false, errors };
}

// src/pricing/booking/subscription.ts
function calculateSubscriptionPrice(baseRate, subscription, currency, durationHours) {
  if (subscription.type === "membership") {
    return createMembershipPrice(baseRate, subscription, currency, durationHours);
  } else if (subscription.type === "pass") {
    return createPassPrice(baseRate, subscription, currency);
  } else {
    return createWalkInPrice(baseRate, currency);
  }
}
function createMembershipPrice(baseRate, subscription, currency, durationHours) {
  if (durationHours && durationHours > 0) {
    const partialPayment = calculateMembershipPartialPayment(
      baseRate,
      durationHours,
      subscription
    );
    if (partialPayment) {
      return {
        amount: partialPayment.walkInAmount,
        currency,
        isFree: false,
        isPartialPayment: true,
        breakdown: {
          baseRate,
          subscriptionDiscount: partialPayment.subscriptionAmount,
          total: partialPayment.walkInAmount,
          subscriptionHours: partialPayment.subscriptionHours,
          walkInHours: partialPayment.walkInHours,
          subscriptionAmount: partialPayment.subscriptionAmount,
          walkInAmount: partialPayment.walkInAmount
        },
        reason: `Partial payment: ${partialPayment.subscriptionHours}h free with ${subscription.membershipName}, ${partialPayment.walkInHours}h at walk-in rate`
      };
    }
  }
  return {
    amount: 0,
    currency,
    isFree: true,
    isPartialPayment: false,
    breakdown: {
      baseRate,
      subscriptionDiscount: baseRate,
      // Full discount - membership covers cost
      total: 0
    },
    reason: `Free booking with ${subscription.membershipName}`
  };
}
function calculateMembershipPartialPayment(baseRate, durationHours, subscription) {
  const constraints = [
    subscription.remainingMinutesToday !== null ? subscription.remainingMinutesToday / 60 : null,
    subscription.remainingMinutesThisMonth !== null ? subscription.remainingMinutesThisMonth / 60 : null,
    subscription.remainingActiveMinutes !== null ? subscription.remainingActiveMinutes / 60 : null
  ].filter((c) => c !== null && c !== void 0);
  if (constraints.length === 0) {
    return null;
  }
  const minRemainingHours = Math.min(...constraints);
  if (minRemainingHours >= durationHours) {
    return null;
  }
  if (minRemainingHours <= 0) {
    return {
      subscriptionHours: 0,
      walkInHours: durationHours,
      subscriptionAmount: 0,
      walkInAmount: baseRate
    };
  }
  const subscriptionHours = minRemainingHours;
  const walkInHours = durationHours - subscriptionHours;
  const hourlyRate = baseRate / durationHours;
  const subscriptionAmount = hourlyRate * subscriptionHours;
  const walkInAmount = hourlyRate * walkInHours;
  return {
    subscriptionHours,
    walkInHours,
    subscriptionAmount,
    walkInAmount
  };
}
function createPassPrice(baseRate, subscription, currency) {
  return {
    amount: 0,
    currency,
    isFree: true,
    isPartialPayment: false,
    breakdown: {
      baseRate,
      subscriptionDiscount: baseRate,
      // Full discount - pass covers cost
      total: 0
    },
    reason: `Free booking with ${subscription.passName}`
  };
}
function createWalkInPrice(baseRate, currency) {
  return {
    amount: baseRate,
    currency,
    isFree: false,
    isPartialPayment: false,
    breakdown: {
      baseRate,
      subscriptionDiscount: 0,
      total: baseRate
    },
    reason: "Walk-in rate"
  };
}

// src/pricing/rates/adapter.ts
function getRateForDuration(duration, rates) {
  if (!rates) return null;
  const key = `${duration}`;
  const rate = rates[key];
  return rate !== null && rate !== void 0 ? rate : null;
}

// src/pricing/booking/setPrice.ts
function calculateSetPrice(duration, defaultRates, primeTimeRates, isPrimeTimeBooking) {
  if (isPrimeTimeBooking && primeTimeRates) {
    const primeRate = getRateForDuration(duration, primeTimeRates);
    if (primeRate !== null) return primeRate;
  }
  const defaultRate = getRateForDuration(duration, defaultRates);
  if (defaultRate !== null) return defaultRate;
  const error = {
    type: "NO_RATE_CONFIGURED",
    message: `No rate configured for ${duration} minutes`
  };
  throw error;
}

// src/pricing/booking/scalingPrice.ts
function calculateScalingPrice(durationMinutes, hourlyRateCents) {
  return Math.round(hourlyRateCents * (durationMinutes / 60));
}

// src/pricing/timeBased/resolver.ts
function resolveTimeBasedRate(defaultRateCents, _config, _bookingStartTimeUtc, _clubTimezone) {
  return defaultRateCents;
}

// src/pricing/dynamic/constants.ts
var LAYER_ORDER = ["add", "multiply"];
var BASIS_POINTS_SCALE = 1e4;
var MINUTES_PER_DAY2 = 1440;

// src/pricing/dynamic/fold.ts
var START_MATCH_OFFSETS = [0, -1];
function anchorBooking(startUtc, timezone) {
  const dayName = getDayNameInTimezone(startUtc, timezone);
  const minutesFromMidnight = getMinutesFromMidnightInTimezone(startUtc, timezone);
  const dayIndex = DAY_NAMES.indexOf(dayName);
  const shiftName = (offset) => DAY_NAMES[(dayIndex + offset + DAY_NAMES.length) % DAY_NAMES.length];
  return {
    minutesFromMidnight,
    dayName,
    dates: {
      [-1]: localDateIso(startUtc, timezone, -1),
      [0]: localDateIso(startUtc, timezone, 0),
      [1]: localDateIso(startUtc, timezone, 1)
    },
    dayNames: {
      [-1]: shiftName(-1),
      [0]: shiftName(0),
      [1]: shiftName(1)
    }
  };
}
function localDateIso(instant, timezone, dayShift) {
  const shifted = new Date(instant.getTime() + dayShift * MINUTES_PER_DAY2 * 6e4);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(shifted);
}
function place(segment, offset) {
  const shift = offset * MINUTES_PER_DAY2;
  return {
    startMinutes: segment.startMinutes + shift,
    endMinutes: segment.endMinutes + shift
  };
}
function covers(window, minutes) {
  return minutes >= window.startMinutes && minutes < window.endMinutes;
}
function findBaseSegment(base, anchor) {
  for (const offset of START_MATCH_OFFSETS) {
    const segments = base[anchor.dayNames[offset]] ?? [];
    for (const segment of segments) {
      if (covers(place(segment, offset), anchor.minutesFromMidnight)) return segment;
    }
  }
  return null;
}
function findMatchingLayers(layers, anchor) {
  const matched = [];
  const seen = /* @__PURE__ */ new Set();
  for (const layer of layers) {
    if (!layer.enabled) continue;
    if (seen.has(layer.id)) continue;
    if (!layerCovers(layer, anchor)) continue;
    seen.add(layer.id);
    matched.push(layer);
  }
  return matched;
}
function layerCovers(layer, anchor) {
  const scope = layer.scope;
  if (scope.kind === "weekly") {
    const days = scope.days;
    return START_MATCH_OFFSETS.some(
      (offset) => (days[anchor.dayNames[offset]] ?? []).some(
        (segment) => covers(place(segment, offset), anchor.minutesFromMidnight)
      )
    );
  }
  return START_MATCH_OFFSETS.some((offset) => {
    if (anchor.dates[offset] !== scope.date) return false;
    return (scope.segments ?? []).some(
      (segment) => covers(place(segment, offset), anchor.minutesFromMidnight)
    );
  });
}

// src/pricing/dynamic/resolveRate.ts
var DynamicRateError = class extends Error {
  constructor(reason, message) {
    super(message);
    this.name = "DynamicRateError";
    this.reason = reason;
  }
};
function resolveRate(input, config, timezone) {
  const anchor = anchorBooking(input.startUtc, timezone);
  if (config.base === null) {
    throw new DynamicRateError(
      "UNCONFIGURED",
      "Club is on dynamicPricing but its rate schedule was never seeded. Refusing to price rather than guessing a rate."
    );
  }
  const baseSegment = findBaseSegment(config.base, anchor);
  if (baseSegment === null) {
    throw new DynamicRateError(
      "NO_BASE_SEGMENT",
      `No base rate covers ${anchor.dayName} ${formatMinutes(anchor.minutesFromMidnight)} (${anchor.dates[0]}). Refusing to price rather than charging nothing.`
    );
  }
  const baseRateCents = baseSegment.rateCents;
  const matched = findMatchingLayers(config.layers, anchor);
  const ordered = [...matched].sort(
    (a, b) => LAYER_ORDER.indexOf(a.kind) - LAYER_ORDER.indexOf(b.kind)
  );
  let running = baseRateCents;
  const contributions = [];
  for (const layer of ordered) {
    running = layer.kind === "add" ? running + layer.value : running * layer.value / BASIS_POINTS_SCALE;
    contributions.push({
      layerId: layer.id,
      label: layer.label,
      kind: layer.kind,
      value: layer.value,
      // Snapshotted rounded, so the stored trail reads in real cents. The
      // running total itself stays exact — see the rounding note above.
      runningRateCentsAfter: Math.round(running)
    });
  }
  const roundedRateCents = Math.round(running);
  const { rateCents: effectiveRateCents, clampApplied } = clampRate(roundedRateCents);
  return { baseRateCents, effectiveRateCents, clampApplied, contributions };
}
function clampRate(rateCents) {
  const { cents, raised, floored } = clampChargeableCents(rateCents);
  return { rateCents: cents, clampApplied: raised || floored };
}
function formatMinutes(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// src/pricing/booking/resolveBasePrice.ts
function resolveBasePrice(durationMinutes, config, bookingStartTimeUtc, options = {}) {
  const { returnDollars = true } = options;
  let priceInCents;
  let tier;
  let isPrimeTime;
  let dayName;
  let matchedSegment;
  let effectiveRateCents;
  let baseRateCents;
  let clampApplied;
  let contributions;
  if (config.mode === "setPrice") {
    const resolved = resolvePrimeTimeForBooking(
      bookingStartTimeUtc,
      config.clubTimezone,
      config.primeTimeConfig,
      config.primeTimeEnabled
    );
    isPrimeTime = resolved.isPrimeTime;
    tier = isPrimeTime ? "primeTime" : "standard";
    dayName = getDayNameInTimezone(bookingStartTimeUtc, config.clubTimezone);
    matchedSegment = String(durationMinutes);
    const dollars = calculateSetPrice(
      durationMinutes,
      config.defaultRates,
      resolved.primeTimeRates,
      isPrimeTime
    );
    priceInCents = dollarsToCents(dollars);
  } else if (config.mode === "scalingPrice") {
    isPrimeTime = false;
    tier = "standard";
    let rateCents = config.hourlyRateCents;
    if (config.timeBasedConfig?.enabled) {
      rateCents = resolveTimeBasedRate(
        rateCents,
        config.timeBasedConfig,
        bookingStartTimeUtc,
        config.clubTimezone
      );
    }
    priceInCents = calculateScalingPrice(durationMinutes, rateCents);
  } else if (config.mode === "dynamicPricing") {
    isPrimeTime = false;
    tier = "dynamic";
    dayName = getDayNameInTimezone(bookingStartTimeUtc, config.clubTimezone);
    const resolved = resolveRate(
      { startUtc: bookingStartTimeUtc},
      config.dynamicConfig,
      config.clubTimezone
    );
    effectiveRateCents = resolved.effectiveRateCents;
    baseRateCents = resolved.baseRateCents;
    clampApplied = resolved.clampApplied;
    contributions = resolved.contributions;
    priceInCents = calculateScalingPrice(durationMinutes, resolved.effectiveRateCents);
  } else {
    const unreachable = config;
    throw new Error(
      `Unhandled pricing mode in resolveBasePrice: ${JSON.stringify(unreachable)}`
    );
  }
  return {
    amount: returnDollars ? centsToDollars(priceInCents) : priceInCents,
    tier,
    isPrimeTime,
    dayName,
    matchedSegment,
    effectiveRateCents,
    baseRateCents,
    clampApplied,
    contributions
  };
}

// src/pricing/booking/buildPricingConfig.ts
function buildPricingConfig(club, clubTimezone) {
  if (club.pricingMode === "scalingPrice") {
    return {
      mode: "scalingPrice",
      hourlyRateCents: club.hourlyRateCents,
      timeBasedConfig: club.timeBasedEnabled ? club.timeBasedPricingConfig ?? void 0 : void 0,
      clubTimezone
    };
  }
  if (club.pricingMode === "setPrice") {
    return {
      mode: "setPrice",
      defaultRates: club.defaultPricing,
      primeTimeConfig: club.primeTimeConfig ?? void 0,
      primeTimeEnabled: club.primeTimeEnabled ?? false,
      clubTimezone
    };
  }
  if (club.pricingMode === "dynamicPricing") {
    return {
      mode: "dynamicPricing",
      dynamicConfig: club.dynamicPricingConfig ?? { base: null, layers: [] },
      hourlyRateCents: club.hourlyRateCents,
      clubTimezone
    };
  }
  throw new Error(
    `Unknown pricingMode: "${club.pricingMode}". Expected "scalingPrice", "setPrice" or "dynamicPricing".`
  );
}

// src/pricing/booking/resolveBayPricing.ts
function resolveBayPricing(club, bay, clubTimezone) {
  if (bay.pricingSource === "custom") return customPricing(bay, clubTimezone);
  return { pricingConfig: buildPricingConfig(club, clubTimezone), source: "club", basis: "booking" };
}
function resolveBayPricingFrom(clubConfig, bay) {
  if (bay.pricingSource === "custom") return customPricing(bay, clubConfig.clubTimezone);
  return { pricingConfig: clubConfig, source: "club", basis: "booking" };
}
function customPricing(bay, clubTimezone) {
  if (bay.customHourlyRateCents == null) {
    throw new Error("Bay is custom-priced but carries no customHourlyRateCents");
  }
  return {
    pricingConfig: { mode: "scalingPrice", hourlyRateCents: bay.customHourlyRateCents, clubTimezone },
    source: "custom",
    basis: bay.pricingBasis
  };
}
function chargedPersons(basis, guestCount) {
  return basis === "person" ? Math.max(0, Math.floor(guestCount)) + 1 : 1;
}

// src/pricing/booking/resolvePaymentRequirement.ts
function resolvePaymentRequirement(quote) {
  switch (quote.status) {
    case "quoted":
      if ((quote.total ?? 0) <= 0) return "not-required";
      if (quote.paymentTiming === "at_club") return "at-club";
      return quote.paymentMethodRequired === false ? "credit-covered" : "required";
    case "pending":
      return "unknown";
    default: {
      const exhaustive = quote;
      return exhaustive;
    }
  }
}

// src/pricing/display/formatters.ts
function formatDuration(minutes) {
  const safeMins = Math.max(0, minutes);
  const hours = Math.floor(safeMins / 60);
  const mins = safeMins % 60;
  if (hours === 0) {
    return `${mins} min`;
  } else if (mins === 0) {
    return `${hours} hr`;
  } else {
    return `${hours} hr ${mins} min`;
  }
}
function formatPrice(price, wholeNumber) {
  const numericPrice = typeof price === "string" ? parseFloat(price) : price;
  if (!isFinite(numericPrice) || isNaN(numericPrice)) {
    return "N/A";
  }
  if (wholeNumber && Number.isInteger(numericPrice)) {
    return `$${numericPrice}`;
  }
  return `$${numericPrice.toFixed(2)}`;
}
function formatTime(time) {
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours) % 24;
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}
function formatHourlyRateLabel(durationMinutes, hourlyRateCents) {
  const durationStr = formatDuration(durationMinutes);
  const rateStr = formatPrice(hourlyRateCents / 100);
  return `${durationStr} \xD7 ${rateStr}`;
}
function formatSetPriceLabel(durationMinutes) {
  return `${durationMinutes} min session`;
}
function formatGuestFeeLabel(chargedGuests, guestFeeTotalCents) {
  const effectiveRateCents = chargedGuests > 0 ? guestFeeTotalCents / chargedGuests : guestFeeTotalCents;
  const rateStr = formatPrice(effectiveRateCents / 100);
  return `${chargedGuests} \xD7 ${rateStr}`;
}
function formatPriceSummary(basePrice, discount, total, userType) {
  if (discount > 0) {
    return `${formatPrice(total)} (${formatPrice(basePrice)} - ${formatPrice(discount)} ${userType} discount)`;
  }
  return formatPrice(total);
}
function formatFeeRate(ratePercent) {
  return `${parseFloat(ratePercent.toFixed(2))}%`;
}

// src/pricing/guestFees/calculateGuestFee.ts
function calculateGuestFee(guestCount, freeGuests, feePerGuest) {
  const chargedGuests = Math.max(0, guestCount - freeGuests);
  return {
    chargedGuests,
    guestFeeTotal: chargedGuests * feePerGuest
  };
}

// src/pricing/guestFees/resolveGuestFeeParams.ts
function resolveGuestFeeParams(guestFeesEnabled, clubConfig, overrides) {
  if (!guestFeesEnabled) {
    return {
      enabled: false,
      freeGuestsPerBooking: 0,
      feePerGuest: 0
    };
  }
  return {
    enabled: true,
    freeGuestsPerBooking: overrides?.freeGuestsOverride ?? clubConfig.freeGuestsPerBooking,
    feePerGuest: overrides?.guestFeeOverride ?? clubConfig.feePerGuest
  };
}
var GuestFeeConfigSchema = z.object({
  freeGuestsPerBooking: z.number().int("Free guests must be a whole number").min(0, "Free guests cannot be negative"),
  feePerGuest: z.number().int("Fee per guest must be a whole number (cents)").min(0, "Fee per guest cannot be negative")
});
var UpdateGuestFeeConfigBodySchema = z.discriminatedUnion("guestFeesEnabled", [
  z.object({
    guestFeesEnabled: z.literal(true),
    guestFeeConfig: GuestFeeConfigSchema
  }),
  z.object({
    guestFeesEnabled: z.literal(false),
    guestFeeConfig: GuestFeeConfigSchema.optional()
  })
]);

// src/pricing/overageDiscount/resolveOverageDiscount.ts
function resolveOverageDiscount(basePriceCents, overageDiscountEnabled, overageDiscountConfig, chargeableMinutes) {
  if (!overageDiscountEnabled || overageDiscountConfig.value === 0) {
    return {
      originalAmountCents: basePriceCents,
      discountedAmountCents: basePriceCents,
      discountApplied: false,
      discountType: null,
      discountValue: 0
    };
  }
  const { discountType, value } = overageDiscountConfig;
  const durationRatio = chargeableMinutes / 60;
  let discountedAmountCents;
  switch (discountType) {
    case "percent": {
      const clampedPercent = Math.min(value, 100);
      discountedAmountCents = Math.round(basePriceCents * (1 - clampedPercent / 100));
      break;
    }
    case "flat":
      discountedAmountCents = Math.round(value * durationRatio);
      break;
    case "subtraction":
      discountedAmountCents = Math.round(basePriceCents - value * durationRatio);
      break;
    default:
      discountedAmountCents = basePriceCents;
      break;
  }
  discountedAmountCents = Math.max(0, discountedAmountCents);
  return {
    originalAmountCents: basePriceCents,
    discountedAmountCents,
    discountApplied: true,
    discountType,
    discountValue: value
  };
}

// src/pricing/invoice/calculators.ts
function round22(n) {
  return Math.round(n * 100) / 100;
}
function deriveOtherPaid({
  total,
  creditApplied,
  amountPaid
}) {
  const received = amountPaid - creditApplied;
  const cap = total - creditApplied;
  const other = total >= 0 ? Math.max(0, Math.min(received, cap)) : Math.min(0, Math.max(received, cap));
  return round22(other) + 0;
}
function lineItemAmount(quantity, unitPrice) {
  return round22(quantity * unitPrice);
}
function sumCharges(lineItems) {
  return round22(
    lineItems.filter((li) => li.type === "charge").reduce((sum, li) => sum + li.amount, 0)
  );
}
function sumDiscounts(lineItems) {
  return round22(
    Math.abs(lineItems.filter((li) => li.type === "discount").reduce((sum, li) => sum + li.amount, 0))
  );
}
function sumAdjustments(lineItems) {
  return round22(
    lineItems.filter((li) => li.type === "adjustment").reduce((sum, li) => sum + li.amount, 0)
  );
}
function resolveLineItemInputs(lineItems) {
  return lineItems.map((li) => ({
    type: li.type ?? "charge",
    amount: lineItemAmount(li.quantity, li.unitPrice)
  }));
}
function netFromLineItemInputs(lineItems) {
  const resolved = resolveLineItemInputs(lineItems);
  return round22(sumCharges(resolved) - sumDiscounts(resolved));
}
function computeTotalsFromLineItems(lineItems, taxRatePercent, settlement = {}, feeRate = null, feeTaxable = false) {
  const subtotal = sumCharges(lineItems);
  const discountTotal = sumDiscounts(lineItems);
  const baseAfterDiscount = round22(Math.max(0, subtotal - discountTotal));
  const feeCalc = feeRate ? calculateProcessingFee(baseAfterDiscount, feeRate) : null;
  const processingFee = feeCalc?.feeAmount ?? 0;
  const processingFeeRate = feeRate?.ratePercent ?? 0;
  const taxableBase = feeTaxable ? round22(baseAfterDiscount + processingFee) : baseAfterDiscount;
  const { taxAmount } = calculateTax(taxableBase, taxRatePercent);
  const adjustmentTotal = sumAdjustments(lineItems);
  const total = round22(baseAfterDiscount + processingFee + taxAmount + adjustmentTotal);
  const creditApplied = round22(settlement.creditApplied ?? 0);
  const amountPaid = round22(settlement.amountPaid ?? 0);
  return {
    subtotal,
    discountTotal,
    tax: taxAmount,
    taxRate: taxRatePercent,
    processingFee,
    processingFeeRate,
    adjustmentTotal,
    total,
    creditApplied,
    amountPaid,
    amountDue: round22(total - amountPaid)
  };
}

// src/pricing/promotion/applyPromotion.ts
var toCents2 = (dollars) => Math.round(dollars * 100);
var fromCents = (cents) => cents / 100;
function applyPromotionCents(unitPriceCents, rule, quantity = 1) {
  const unitPrice = Math.round(unitPriceCents);
  const validQuantity = Number.isInteger(quantity) && quantity >= 1;
  if (!Number.isFinite(unitPrice) || unitPrice <= 0 || !validQuantity) {
    const safeUnit = Number.isFinite(unitPrice) ? unitPrice : 0;
    const safeQuantity = validQuantity ? quantity : 1;
    const safeList = safeUnit * safeQuantity;
    return {
      listAmountCents: safeList,
      discountAmountCents: 0,
      unitDiscountAmountCents: 0,
      quantity: safeQuantity,
      netAmountCents: Math.max(0, safeList),
      clamped: false,
      free: false
    };
  }
  const listAmountCents = unitPrice * quantity;
  let unitDiscountAmountCents;
  let clamped = false;
  if (rule.benefitType === "percent_off") {
    const pct = Math.min(Math.max(rule.percentOff, 0), 100);
    unitDiscountAmountCents = Math.round(unitPrice * pct / 100);
  } else {
    const requested = Math.round(Math.max(rule.amountOffCents, 0));
    clamped = requested > unitPrice;
    unitDiscountAmountCents = clamped ? unitPrice : requested;
  }
  const discountAmountCents = unitDiscountAmountCents * quantity;
  const netAmountCents = listAmountCents - discountAmountCents;
  return {
    listAmountCents,
    discountAmountCents,
    unitDiscountAmountCents,
    quantity,
    netAmountCents,
    clamped,
    free: netAmountCents === 0 && discountAmountCents > 0
  };
}
function applyPromotion(unitPriceDollars, rule, quantity = 1) {
  const ruleCents = rule.benefitType === "percent_off" ? rule : { benefitType: "amount_off", amountOffCents: toCents2(rule.amountOff) };
  const applied = applyPromotionCents(toCents2(unitPriceDollars), ruleCents, quantity);
  return {
    listAmount: fromCents(applied.listAmountCents),
    discountAmount: fromCents(applied.discountAmountCents),
    unitDiscountAmount: fromCents(applied.unitDiscountAmountCents),
    quantity: applied.quantity,
    netAmount: fromCents(applied.netAmountCents),
    clamped: applied.clamped,
    free: applied.free
  };
}
var RatedSegmentSchema = z.object({
  startMinutes: z.number().int("Start minutes must be an integer").min(0, "Start minutes cannot be negative").max(1439, "Start minutes cannot exceed 1439 (11:59 PM)"),
  endMinutes: z.number().int("End minutes must be an integer").min(0, "End minutes cannot be negative").max(1560, "End minutes cannot exceed 1560 (2:00 AM next day)"),
  rateCents: z.number().int("Rate must be an integer number of cents").min(0, "A base rate cannot be negative")
}).refine((s) => s.startMinutes < s.endMinutes, {
  message: "Start time must be before end time"
}).refine((s) => s.endMinutes <= s.startMinutes + 1440, {
  message: "A window cannot be longer than 24 hours"
});
var RatedDayScheduleSchema = z.array(RatedSegmentSchema).refine(
  (segments) => {
    const sorted = [...segments].sort((a, b) => a.startMinutes - b.startMinutes);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].startMinutes < sorted[i - 1].endMinutes) return false;
    }
    return true;
  },
  { message: "Base rate segments must not overlap" }
);
var RateScheduleSchema = z.object({
  monday: RatedDayScheduleSchema,
  tuesday: RatedDayScheduleSchema,
  wednesday: RatedDayScheduleSchema,
  thursday: RatedDayScheduleSchema,
  friday: RatedDayScheduleSchema,
  saturday: RatedDayScheduleSchema,
  sunday: RatedDayScheduleSchema
});
var RateLayerScopeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("weekly"),
    days: WeeklyScheduleSchema
  }),
  z.object({
    kind: z.literal("date"),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD"),
    segments: z.array(TimeSegmentSchema)
  })
]);
var RateLayerSchema = z.object({
  id: z.string().min(1, "A layer needs a stable id"),
  label: z.string().min(1, "A layer needs a label \u2014 the booking snapshot stores it"),
  scope: RateLayerScopeSchema,
  kind: z.enum(["add", "multiply"]),
  value: z.number().int("Layer values are integers \u2014 cents, or basis points").nonnegative("Layers only ever raise a rate; discounting belongs to promotions"),
  enabled: z.boolean()
}).refine((l) => l.kind !== "multiply" || l.value > 0, {
  message: `A multiply layer must be a positive number of basis points (${BASIS_POINTS_SCALE} = x1)`,
  path: ["value"]
});
var DynamicPricingConfigSchema = z.object({
  base: RateScheduleSchema.nullable(),
  layers: z.array(RateLayerSchema)
}).refine(
  (c) => new Set(c.layers.map((l) => l.id)).size === c.layers.length,
  { message: "Layer ids must be unique", path: ["layers"] }
);
var UNCONFIGURED_DYNAMIC_PRICING = Object.freeze({
  base: null,
  layers: []
});
var DayRateOverrideSchema = z.object({
  offPeakCents: z.number().int().min(0),
  peakCents: z.number().int().min(0)
});
var TimeBasedScheduleSchema = WeeklyScheduleSchema.refine(
  (schedule) => Object.values(schedule).every((day) => day.length <= 1),
  { message: "Time-based pricing allows at most 1 peak segment per day" }
);
var WeeklyRatesSchema = z.object({
  monday: DayRateOverrideSchema,
  tuesday: DayRateOverrideSchema,
  wednesday: DayRateOverrideSchema,
  thursday: DayRateOverrideSchema,
  friday: DayRateOverrideSchema,
  saturday: DayRateOverrideSchema,
  sunday: DayRateOverrideSchema
});
var TimeBasedPricingConfigSchema = z.object({
  enabled: z.boolean(),
  peakHours: TimeBasedScheduleSchema,
  rates: WeeklyRatesSchema
});

// src/pricing/subscription/deriveSubscriptionQuote.ts
function deriveSubscriptionQuote(inputs) {
  const { quoteEnabled, isQuoting, quoteFailed, hasQuoteData, total, quoteRefused, paymentMethodRequired } = inputs;
  if (!quoteEnabled || isQuoting || quoteFailed || !hasQuoteData || quoteRefused) {
    return { status: "pending" };
  }
  return { status: "quoted", total, ...paymentMethodRequired !== void 0 && { paymentMethodRequired } };
}

// src/pricing/lessons/resolveLessonBasePrice.ts
function resolveLessonBasePrice(hourlyRateCents, durationMinutes) {
  return Math.round(hourlyRateCents * (durationMinutes / 60));
}

// src/pricing/lessons/pickLessonMembershipDiscount.ts
function pickLessonMembershipDiscount(listPriceCents, candidates) {
  if (listPriceCents <= 0) return null;
  let best = null;
  for (const candidate of candidates) {
    const applied = applyPromotionCents(listPriceCents, candidate.rule);
    const beats = best === null || applied.discountAmountCents > best.discountCents || applied.discountAmountCents === best.discountCents && candidate.membershipId < best.candidate.membershipId;
    if (beats) {
      best = {
        candidate,
        discountCents: applied.discountAmountCents,
        netCents: applied.netAmountCents
      };
    }
  }
  return best && best.discountCents > 0 ? best : null;
}

export { BASIS_POINTS_SCALE, DEFAULT_CURRENCY, DEFAULT_PROCESSING_FEE_CONFIG, DEFAULT_PROCESSING_FEE_RATE, DynamicPricingConfigSchema, DynamicRateError, GuestFeeConfigSchema, LAYER_ORDER, PRICE_MAX_DOLLARS, PRICE_MAX_MESSAGE, PROCESSING_FEE_TAXABLE, PROVIDER_MIN_CHARGE_AMOUNT_CENTS, PROVIDER_MIN_CHARGE_AMOUNT_DOLLARS, PROVIDER_MIN_CHARGE_MESSAGE, PrimeTimeConfigSchema, RateLayerSchema, RateScheduleSchema, RatesByDurationSchema, TimeBasedPricingConfigSchema, UNCONFIGURED_DYNAMIC_PRICING, UpdateGuestFeeConfigBodySchema, applyPromotion, applyPromotionCents, buildPricingConfig, calculateDurationWithMidnight, calculateGuestFee, calculateProcessingFee, calculateScalingPrice, calculateSetPrice, calculateSubscriptionPrice, calculateTax, centsToDollars, chargedPersons, clampChargeableCents, computeTotalsFromLineItems, crossesMidnight, deriveOtherPaid, deriveSubscriptionQuote, dollarsToCents, formatDuration, formatFeeRate, formatGuestFeeLabel, formatHourlyRateLabel, formatPrice, formatPriceFromCents, formatPriceSummary, formatSetPriceLabel, formatTime, getProcessingFeeConfig, getRateForDuration, isProcessingFeeTaxable, lineItemAmount, minutesToTime, netFromLineItemInputs, pickLessonMembershipDiscount, representativeHourlyRateCents, resolveBasePrice, resolveBayPricing, resolveBayPricingFrom, resolveGuestFeeParams, resolveLessonBasePrice, resolveLineItemInputs, resolveOverageDiscount, resolvePaymentRequirement, resolvePrimeTimeForBooking, resolveQuoteProcessingFee, resolveRate, resolveTimeBasedRate, round22 as round2, sizeCardCharge, sumAdjustments, sumCharges, sumDiscounts, validatePrimeTimeConfig, validateRates };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map